import { createContext, useCallback, useContext, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, MapPin, ArrowRight, ShieldCheck, CheckCircle2 } from "lucide-react";
import { LICENSED_STATES } from "@/lib/constants";
import { mloPhotoUrl } from "@/lib/photo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATES = LICENSED_STATES.split(",").map((s) => s.trim()).filter(Boolean);

const RateCheckRouterContext = createContext(null);

export function useRateCheckRouter() {
  const ctx = useContext(RateCheckRouterContext);
  if (!ctx) throw new Error("useRateCheckRouter must be used inside <RateCheckRouterProvider>");
  return ctx;
}

const label = "text-[11px] font-semibold uppercase tracking-widest text-[#59564C]";
const field =
  "mt-1 w-full rounded-xl border border-[#E6E2D6] focus:border-[#1F8F8B] focus:outline-none focus:ring-2 focus:ring-[#1F8F8B]/20 p-3 text-sm text-[#0F2A47] bg-white";

function OfficerCard({ officer, onPick }) {
  return (
    <button
      type="button"
      onClick={() => onPick(officer)}
      data-testid={`router-officer-${officer.slug}`}
      className="w-full text-left flex items-center gap-3 rounded-2xl border border-[#E6E2D6] hover:border-[#1F8F8B] hover:bg-[#1F8F8B]/5 p-3 transition-colors group"
    >
      <div className="w-14 h-16 rounded-xl overflow-hidden bg-[#0F2A47] shrink-0">
        {officer.has_photo ? (
          <img
            src={mloPhotoUrl(officer)}
            alt={officer.name}
            className="w-full h-full object-cover object-top"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/50 font-semibold">
            {(officer.name || "?").slice(0, 1)}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-[#0F2A47] truncate">{officer.name}</div>
        <div className="text-[11px] text-[#59564C] truncate">
          {officer.city || officer.title || "Mortgage Loan Officer"}
          {officer.nmls ? ` · NMLS #${officer.nmls}` : ""}
        </div>
        {officer.tagline ? (
          <div className="text-[11px] text-[#7A7668] truncate italic mt-0.5">{officer.tagline}</div>
        ) : null}
      </div>
      <ArrowRight className="w-4 h-4 text-[#1F8F8B] shrink-0 group-hover:translate-x-0.5 transition-transform" />
    </button>
  );
}

export function RateCheckRouterProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState("");
  const [step, setStep] = useState("where"); // where | pick | capture | done
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [officers, setOfficers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ full_name: "", email: "", phone: "" });

  const openRouter = useCallback((src) => {
    setSource(src || "unknown");
    setStep("where");
    setState("");
    setZip("");
    setOfficers([]);
    setErr("");
    setForm({ full_name: "", email: "", phone: "" });
    setOpen(true);
  }, []);

  const close = () => setOpen(false);

  const goToOfficer = (officer) => {
    window.location.href = `/${officer.slug}/apply`;
  };

  const findOfficers = async () => {
    if (!state) {
      setErr("Pick the state the property is in so we match you with a licensed officer.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const { data } = await axios.get(`${API}/match/officers`, { params: { state } });
      const list = data.officers || [];
      setOfficers(list);
      if (list.length === 1) {
        setStep("pick");
      } else if (list.length > 1) {
        setStep("pick");
      } else {
        setStep("capture");
      }
    } catch (e) {
      setErr("Couldn't look that up just now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const submitUnmatched = async () => {
    if (!form.full_name.trim() || !form.email.trim() || !form.phone.trim()) {
      setErr("Please add your name, email, and phone so we can reach you.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await axios.post(`${API}/leads`, {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        state,
        property_state: state,
        property_zip: zip.trim(),
        consent: true,
        source: `house_router:${source}`,
      });
      setStep("done");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <RateCheckRouterContext.Provider value={{ openRouter }}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-black/55 backdrop-blur-sm flex items-center justify-center px-4"
            data-testid="rate-check-router"
            onClick={close}
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.96 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden"
            >
              <div className="px-6 pt-6 pb-4 relative">
                <button
                  type="button"
                  onClick={close}
                  aria-label="Close"
                  data-testid="router-close"
                  className="absolute top-4 right-4 p-2 rounded-lg hover:bg-[#F4F2EB] text-[#7A7668]"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.22em] uppercase text-[#1F8F8B]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#A6D24A]" />
                  {step === "done" ? "You're all set" : "Match me with an officer"}
                </div>

                {step === "where" && (
                  <>
                    <h2 className="mt-3 font-heading text-2xl leading-tight text-[#0F2A47]">
                      Where's the property?
                    </h2>
                    <p className="mt-2 text-sm text-[#59564C] leading-relaxed">
                      Loan officers are licensed state by state — this makes sure you get one who
                      can actually do your loan.
                    </p>
                  </>
                )}

                {step === "pick" && (
                  <>
                    <h2 className="mt-3 font-heading text-2xl leading-tight text-[#0F2A47]">
                      {officers.length === 1
                        ? `You're matched with ${officers[0].name.split(" ")[0]}.`
                        : "Choose who you'd like to work with."}
                    </h2>
                    <p className="mt-2 text-sm text-[#59564C] leading-relaxed">
                      {officers.length === 1
                        ? `Licensed in ${state} and ready to run your numbers — one officer, start to close.`
                        : `${officers.length} officers in the network are licensed in ${state}.`}
                    </p>
                  </>
                )}

                {step === "capture" && (
                  <>
                    <h2 className="mt-3 font-heading text-2xl leading-tight text-[#0F2A47]">
                      We'll find you the right officer.
                    </h2>
                    <p className="mt-2 text-sm text-[#59564C] leading-relaxed">
                      We're still adding licensed officers in {state}. Leave your details and the
                      Great Rate Check team will personally match you — usually same business day.
                    </p>
                  </>
                )}

                {step === "done" && (
                  <>
                    <h2 className="mt-3 font-heading text-2xl leading-tight text-[#0F2A47]">
                      Got it — we're on it.
                    </h2>
                    <p className="mt-2 text-sm text-[#59564C] leading-relaxed">
                      A licensed officer will reach out about your {state} property shortly. Keep
                      an eye on your email and phone.
                    </p>
                  </>
                )}
              </div>

              <div className="px-6 pb-4 space-y-3">
                {step === "where" && (
                  <>
                    <label className="block">
                      <span className={label}>Property state</span>
                      <select
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        data-testid="router-state"
                        className={field}
                      >
                        <option value="">Select a state…</option>
                        {STATES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className={label}>ZIP code (optional)</span>
                      <input
                        value={zip}
                        onChange={(e) => setZip(e.target.value.replace(/[^0-9-]/g, "").slice(0, 10))}
                        placeholder="98033"
                        data-testid="router-zip"
                        className={field}
                      />
                    </label>
                  </>
                )}

                {step === "pick" && (
                  <div className="space-y-2" data-testid="router-officer-list">
                    {officers.map((o) => (
                      <OfficerCard key={o.slug} officer={o} onPick={goToOfficer} />
                    ))}
                  </div>
                )}

                {step === "capture" && (
                  <>
                    <label className="block">
                      <span className={label}>Full name</span>
                      <input
                        autoFocus
                        value={form.full_name}
                        onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                        placeholder="Jane Homeowner"
                        data-testid="router-name"
                        className={field}
                      />
                    </label>
                    <label className="block">
                      <span className={label}>Email</span>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        placeholder="you@example.com"
                        data-testid="router-email"
                        className={field}
                      />
                    </label>
                    <label className="block">
                      <span className={label}>Phone</span>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                        placeholder="(555) 555-1234"
                        data-testid="router-phone"
                        className={field}
                      />
                    </label>
                  </>
                )}

                {err && (
                  <p className="text-xs text-red-600" data-testid="router-error">{err}</p>
                )}

                {step !== "done" && (
                  <p className="text-[11px] text-[#7A7668] flex items-center gap-1.5 pt-1">
                    <ShieldCheck className="w-3.5 h-3.5 text-[#1F8F8B]" />
                    Your info stays private. No sales calls, no spam.
                  </p>
                )}
              </div>

              <div className="px-6 pb-6 pt-2 bg-[#FDFBF7] flex flex-col gap-2">
                {step === "where" && (
                  <button
                    type="button"
                    onClick={findOfficers}
                    disabled={busy}
                    data-testid="router-find"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#A6D24A] hover:bg-[#95c13a] text-[#0F2A47] font-semibold text-sm px-5 py-3 shadow disabled:opacity-70"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                    Find my loan officer
                  </button>
                )}

                {step === "pick" && (
                  <button
                    type="button"
                    onClick={() => setStep("where")}
                    data-testid="router-back"
                    className="text-xs text-[#7A7668] hover:text-[#0F2A47] underline decoration-[#D9D3BF] underline-offset-4 py-1"
                  >
                    Change state
                  </button>
                )}

                {step === "capture" && (
                  <button
                    type="button"
                    onClick={submitUnmatched}
                    disabled={busy}
                    data-testid="router-submit"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#A6D24A] hover:bg-[#95c13a] text-[#0F2A47] font-semibold text-sm px-5 py-3 shadow disabled:opacity-70"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Match me with an officer
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}

                {step === "done" && (
                  <button
                    type="button"
                    onClick={close}
                    data-testid="router-done"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0F2A47] hover:bg-[#173a5f] text-white font-semibold text-sm px-5 py-3"
                  >
                    <CheckCircle2 className="w-4 h-4" /> Done
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </RateCheckRouterContext.Provider>
  );
}
